import { zodResolver } from '@hookform/resolvers/zod'
import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId, useConfig } from 'wagmi'

import { createTxOverview, TxOverview } from './createTxOverview'
import { getFormFieldsForUnstakeDialog } from './getFormFieldsForUnstakeDialog'
import { getUnstakeDialogFormValidator } from './validation'
import { getChainConfigEntry } from '@/config/chain'
import { TokenWithBalance } from '@/domain/common/types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { UnstakeSpkObjective } from '@/features/actions/flavours/unstake-spk/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { useDebouncedFormValues } from '@/features/dialogs/common/logic/transfer-from-user/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { useSpkStakingData } from '@/features/spk/staking/logic/useSpkStakingData'

export interface UseUnstakeDialogResult {
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  actionsViewState: ActionsViewState
  spk: Token
  staked: NormalizedNumber
  pageStatus: PageStatus
  txOverview: TxOverview
}

export function useUnstakeDialog(): UseUnstakeDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const chainId = useChainId()
  const { account } = useConnectedAddress()
  const wagmiConfig = useConfig()

  const sparkTokenFeatureConfig = getChainConfigEntry(chainId).spk
  assert(sparkTokenFeatureConfig, 'Spark token config should be defined')

  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))

  const { spkStakingData } = useSpkStakingData({
    chainId,
    account,
    wagmiConfig,
    tokenRepository,
  })

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getUnstakeDialogFormValidator({ stakedAmount: spkStakingData.amountStaked })),
    defaultValues: {
      symbol: spk.symbol,
      value: '',
    },
    mode: 'onChange',
  })
  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: UnstakeSpkObjective[] = [
    {
      type: 'unstakeSpk',
      spk,
      amount: formValues.value,
      accountActiveShares: spk.toBaseNumber(spkStakingData.amountStaked),
      unstakeAll: formValues.isMaxSelected,
    },
  ]

  const txOverview = createTxOverview({
    amountStaked: spkStakingData.amountStaked,
    nextEpochEnd: spkStakingData.nextEpochEnd,
    formValues,
  })

  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing

  const assetsFields = getFormFieldsForUnstakeDialog({
    form,
    tokenRepository,
    stakedAmount: spkStakingData.amountStaked,
  })

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  return {
    spk,
    staked: formValues.value,
    selectableAssets: [{ token: spk, balance: spkStakingData.amountStaked }],
    assetsFields,
    form,
    actionsViewState,
    txOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
  }
}
